import '../../styles/Details.scss';

import React from 'react';
import { format } from 'date-fns';
import {Link, useParams} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { auth } from '../../firebase-config';
import { SeriesModel } from '../../models';
import { setBackgroundImage } from '../../helpers/helpers';

import InfoList from "../../components/InfoList";
import Loader from '../../components/Loader';
import PersonList from '../../components/PersonList';
import Ratings from '../../components/Ratings';

const TvDetails = () => {
    const { id } = useParams();

    const getTvDetails = async (id: string | undefined) => {
        const apiKey = process.env.REACT_APP_TMDB_API_KEY;
        const response = await fetch(`https://api.themoviedb.org/3/tv/${id}?language=en-GB&append_to_response=aggregate_credits`, {
            headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${apiKey}`,
            }
        });
        return await response.json();
    }

    const { data: tvDetails, isLoading, error } = useQuery<SeriesModel>({
        queryKey: ['tv-details', id],
        queryFn: () => getTvDetails(id),
        enabled: !!id, // Only run query if id exists
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    if (isLoading || !tvDetails) return <Loader fullscreen message="Loading..." />;
    if (error) return <div>Error loading TV show details.</div>;

    setBackgroundImage(tvDetails.backdrop_path);
    tvDetails.media_type = 'tv';
    const pageTitle = `${tvDetails.name} (${new Date(tvDetails.first_air_date).getFullYear()})`;

    const infoListItems = [
        { label: 'Premiered', value: tvDetails.first_air_date ? format(new Date(tvDetails.first_air_date), 'd MMMM yyyy') : null },
        { label: 'Status', value: tvDetails.status },
        { 
            label: 'Created By',
            value: tvDetails.created_by?.map(creator => ({
                id: creator.id,
                name: creator.name,
            })) || []
        },
        { label: 'Genres', value: tvDetails.genres?.map(genre => genre.name).join(', ') },
    ];

    document.title = pageTitle;

    return (
        <>
            <div>
                <div className="details-summary-container">
                    <div className="details-poster">
                        <img src={`https://image.tmdb.org/t/p/w500${tvDetails.poster_path}`} alt={tvDetails.name} />
                        {auth.currentUser && (
                            <>
                                <button className="history-button">Add to History</button>
                            </>
                        )}
                    </div>

                    <div className="details-summary-content">
                        <div className="content-children">
                            <div className="title-container">
                                <h1 className="details-title">{tvDetails.name}</h1>
                                <Ratings details={tvDetails} link={`tv/${tvDetails.id}`} />
                            </div>

                        </div>

                        <p className="details-overview">{tvDetails.overview}</p>
                    </div>
                </div>

                <div className="details-summary-container">
                    <div className="details-summary-content">
                        <InfoList items={infoListItems} />
                    </div>
                </div>
            </div>

            <div className="details-person-container">
                <PersonList people={tvDetails.aggregate_credits?.cast || []} />
            </div>

            {tvDetails.seasons && (
                <div className="seasons-container">
                    {tvDetails.seasons.length === 1 && <h2>1 Season</h2>}
                    {tvDetails.seasons.length > 1 && <h2>{tvDetails.seasons.length} Seasons</h2>}
                    <div className='seasons'>
                        {tvDetails.seasons.slice().reverse().map(season => (
                            <Link to={`season/${season.season_number}`} key={season.id} className='season-card'>
                                {season.poster_path ? (
                                    <img src={`https://image.tmdb.org/t/p/w500${season.poster_path}`} alt={season.name}/>
                                ) : (
                                    <img src={`https://image.tmdb.org/t/p/w500${tvDetails.poster_path}`} alt={season.name}/>
                                )}
                                <div className='season-info'>
                                    <h3>{season.name}</h3>
                                    <h4>{season.episode_count} Episodes</h4>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default TvDetails;
